import React from 'react'

import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip'

import type { TooltipProps } from './antd-props'
import { needsDisabledTriggerWrapper } from './overlay/disabledTrigger'
import {
  AntdPlacement,
  normaliseTriggers,
  omitTransient,
  toRadixSideAlign,
} from './overlay/placement'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Tooltip` seam. `./Tooltip.tsx` is the
 * dispatcher that chooses between this and antd's own Tooltip; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * The external API is antd's own `TooltipProps`, so the 117 files that import
 * `Tooltip` from `src/antd` — and `WHTooltip`, which wraps it — switch with no
 * edit at all.
 *
 * ---------------------------------------------------------------------------
 * THE CONTAINER DECISION, stated once for both overlay seams
 *
 * The bubble is PORTALLED TO `document.body`, which is antd's default too, and
 * it stays there unless a call site explicitly hands over a
 * `getPopupContainer`. It is NOT pinned to the trigger's parent.
 *
 * That is deliberately the opposite of what `src/antd/Select` does. Select
 * defaults `getPopupContainer` to the trigger's parent, which takes the popup
 * out of the body portal and puts it inside whatever the trigger happens to
 * sit in — so an ancestor with `overflow: hidden`, a stacking context or a
 * `transform` can then clip it. There is an open task about exactly that class
 * of bug for 23 Selects and 3 Dropdowns; a tooltip is a smaller target and a
 * denser surface (297 direct call sites, most of them inside scrolling tables
 * and side panels), so inheriting that failure mode would be worse, not
 * better. Body-portalled means an overflow ancestor cannot clip it, and Radix
 * keeps the bubble positioned against the trigger through scroll.
 *
 * `getPopupContainer` is still honoured when a call site passes one — no
 * live call site does today, which is precisely why the default is the whole
 * decision — but it is accepted rather than silently dropped, so a future call
 * site gets the behaviour its name promises.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS MAPPED
 *   • falsy `title` AND falsy `overlay` (antd's own `noTitle` test, which
 *     carves out `0`) → children rendered bare, as antd
 *   • `trigger` → hover/focus; `[]` switches the tooltip off entirely
 *   • `mouseEnterDelay` (seconds) → Radix `delayDuration` (ms)
 *   • compound `placement` → Radix `side`/`align` (antd's default is `top`)
 *   • `color` / `overlayClassName` / `overlayStyle` / `overlayInnerStyle` /
 *     `zIndex` land on the Radix Content (antd's separate overlay + inner
 *     nodes collapse to one)
 *   • `autoAdjustOverflow={false}` → `avoidCollisions={false}`
 *   • `arrow={false}` → no arrow
 *   • controlled `open`/`onOpenChange` and the legacy v4 `visible`/
 *     `onVisibleChange` aliases are forwarded to the Radix Root
 *
 * WHAT IS NOT — named rather than pretended:
 *   • `trigger: 'click' | 'contextMenu'` — Radix's tooltip has no click mode.
 *     A tooltip asking only for those falls back to hover rather than going
 *     silently dead. No call site does; a click-opened bubble is a Popover.
 *   • `mouseLeaveDelay` — Radix has no close delay. No call site sets it.
 *   • `align` (rc-trigger's raw align object), `builtinPlacements`,
 *     `arrowPointAtCenter`, `openClassName`, `afterOpenChange` — no call site
 *     sets any of them.
 *   • `destroyTooltipOnHide` is a no-op: Radix already unmounts on close.
 *
 * KNOWN DIVERGENCE: Radix opens on keyboard FOCUS as well as hover, where
 * antd's default `trigger: 'hover'` does not. That is an accessibility
 * improvement, and it is the reason `trigger={[]}` had to keep working.
 * ------------------------------------------------------------------------- */

export type { TooltipProps }

const Tooltip = ({
  title,
  overlay,
  children,
  placement,
  trigger,
  open,
  defaultOpen,
  onOpenChange,
  // legacy v4 aliases
  visible,
  onVisibleChange,
  mouseEnterDelay,
  color,
  overlayClassName,
  overlayStyle,
  overlayInnerStyle,
  autoAdjustOverflow,
  arrow,
  zIndex,
  getPopupContainer,
  // accepted, then dropped so they never reach the DOM — see the header
  destroyTooltipOnHide: _destroyTooltipOnHide,
  mouseLeaveDelay: _mouseLeaveDelay,
  align: _align,
  builtinPlacements: _builtinPlacements,
  arrowPointAtCenter: _arrowPointAtCenter,
  openClassName: _openClassName,
  afterOpenChange: _afterOpenChange,
  afterVisibleChange: _afterVisibleChange,
  ...rest
}: TooltipProps): JSX.Element => {
  // The trigger node, only ever needed to answer `getPopupContainer`. State
  // rather than a bare ref so the portal re-targets once the node exists.
  const [triggerNode, setTriggerNode] = React.useState<HTMLElement | null>(null)

  // antd 5.15.4's own two rules, transcribed rather than approximated
  // (`antd/es/tooltip/index.js`): `noTitle` is falsy-title-and-falsy-overlay
  // with `0` carved out, and the rendered content prefers `overlay`. The
  // difference is load-bearing — the semantic button kit passes
  // `title={iconOnly && children}`, so every call site with a computed
  // `iconOnly` hands this seam `title={false}` and must open nothing.
  const noTitle = !title && !overlay && title !== 0
  const resolvedTitle = title === 0 ? title : overlay || title || ''

  const triggers = normaliseTriggers(
    trigger as Parameters<typeof normaliseTriggers>[0],
    'hover'
  )

  // antd renders children bare when there is nothing to show — and this repo
  // switches a tooltip off with `trigger={[]}`, which must do the same.
  if (noTitle || triggers.size === 0) {
    return <>{children}</>
  }

  const { side, align } = toRadixSideAlign(
    placement as AntdPlacement | undefined,
    'top'
  )

  const controlledOpen = open ?? visible
  const handleOpenChange = onOpenChange ?? onVisibleChange

  const contentStyle: React.CSSProperties = {
    ...(color ? { backgroundColor: color } : {}),
    ...(overlayStyle as React.CSSProperties | undefined),
    ...(overlayInnerStyle as React.CSSProperties | undefined),
    ...(zIndex != null ? { zIndex } : {}),
  }

  const delayDuration =
    mouseEnterDelay != null ? Math.round(mouseEnterDelay * 1000) : undefined

  // A natively disabled control emits no pointer events, so under `asChild` it
  // is a dead trigger and needs a box to hover instead. The test is
  // deliberately narrow — `needsDisabledTriggerWrapper` says at length why a
  // wider one collapses the call site's layout. A NON-ELEMENT child (a string,
  // a fragment) needs a host element for `asChild` to attach to at all; antd
  // wraps that case in a bare `<span>`, so this does too.
  let triggerChild: React.ReactNode
  if (!React.isValidElement(children)) {
    triggerChild = <span>{children}</span>
  } else if (needsDisabledTriggerWrapper(children)) {
    triggerChild = <span style={{ display: 'inline-block' }}>{children}</span>
  } else {
    triggerChild = children
  }

  return (
    // Self-provide a TooltipProvider so the seam works in ANY tree — antd's
    // Tooltip needs no provider, and an isolated component or test render does
    // not wrap one. Radix allows nested providers, so an app-level provider (if
    // one is ever added) still holds for everything else.
    <TooltipProvider delayDuration={delayDuration}>
      <UITooltip
        open={controlledOpen}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        delayDuration={delayDuration}
      >
        <TooltipTrigger asChild ref={setTriggerNode}>
          {triggerChild}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          avoidCollisions={autoAdjustOverflow !== false}
          showArrow={arrow !== false}
          className={overlayClassName}
          style={contentStyle}
          container={
            getPopupContainer && triggerNode
              ? getPopupContainer(triggerNode)
              : undefined
          }
          {...omitTransient(rest as Record<string, unknown>)}
        >
          {resolvedTitle}
        </TooltipContent>
      </UITooltip>
    </TooltipProvider>
  )
}

export { Tooltip }
